import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import Modal from './Modal';
import './ConfirmDialog.css';

const ConfirmContext = createContext(null);

export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
};

export const ConfirmProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);
  const resolveRef = useRef(null);

  const confirm = useCallback(({ title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel', variant = 'default' } = {}) => {
    return new Promise((resolve) => {
      // If a previous confirm is still pending, settle it as cancelled —
      // otherwise its promise never resolves and its awaiter hangs forever.
      if (resolveRef.current) resolveRef.current(false);
      resolveRef.current = resolve;
      setDialog({ title, message, confirmLabel, cancelLabel, variant });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setDialog(null);
    if (resolveRef.current) {
      resolveRef.current(true);
      resolveRef.current = null;
    }
  }, []);

  const handleCancel = useCallback(() => {
    setDialog(null);
    if (resolveRef.current) {
      resolveRef.current(false);
      resolveRef.current = null;
    }
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialog && (
        <Modal onClose={handleCancel} titleId="confirm-title" className="confirm-overlay-wrap">
          <div className="confirm-dialog">
            {dialog.title && (
              <div id="confirm-title" className="confirm-title">
                <i className={`fas ${dialog.variant === 'danger' ? 'fa-exclamation-triangle' : 'fa-question-circle'}`} aria-hidden="true" />
                {dialog.title}
              </div>
            )}
            <div className="confirm-message">{dialog.message}</div>
            <div className="confirm-actions">
              <button className="confirm-btn confirm-btn-cancel" onClick={handleCancel} autoFocus>
                {dialog.cancelLabel}
              </button>
              <button className={`confirm-btn confirm-btn-confirm ${dialog.variant === 'danger' ? 'confirm-btn-danger' : ''}`} onClick={handleConfirm}>
                {dialog.confirmLabel}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </ConfirmContext.Provider>
  );
};

export default ConfirmProvider;